import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, Download, Copy, Eye, QrCode } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function QRCodeManager() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', target_url: '' });
  const [previewItem, setPreviewItem] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [previewScanUrl, setPreviewScanUrl] = useState(null);
  const [previewStyle, setPreviewStyle] = useState('framed');

  // Fetch the preview PNG as a blob (the endpoint is auth-required, so a raw
  // <img src=> won't work — browsers can't attach the Authorization header).
  useEffect(() => {
    if (!previewItem) {
      setPreviewSrc(null);
      setPreviewScanUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl = null;
    setPreviewScanUrl(null);
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/qr-codes/${previewItem.id}/image.png?style=${previewStyle}`, {
          ...authHeader,
          responseType: 'blob',
        });
        if (cancelled) return;
        const blob = new Blob([res.data], { type: 'image/png' });
        objectUrl = URL.createObjectURL(blob);
        setPreviewSrc(objectUrl);
        // The backend echoes the embedded scan URL in a custom header — surface
        // it so the admin can click-to-test before printing the QR.
        const scanUrl = res.headers?.['x-qr-scan-url'] || res.headers?.['X-QR-Scan-URL'];
        if (scanUrl) setPreviewScanUrl(scanUrl);
      } catch {
        if (!cancelled) toast.error('Failed to load QR preview');
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line
  }, [previewItem, previewStyle]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/qr-codes`, authHeader);
      setItems(res.data?.qr_codes || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("You don't have permission to manage QR codes.");
        navigate('/');
      } else {
        toast.error('Failed to load QR codes');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [token, navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', target_url: '' });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ name: item.name, target_url: item.target_url });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    const url = form.target_url.trim();
    if (!/^https?:\/\//.test(url)) return toast.error('Target URL must start with http:// or https://');
    try {
      if (editing) {
        await axios.patch(`${BACKEND_URL}/api/qr-codes/${editing.id}`, form, authHeader);
        toast.success('QR code updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/qr-codes`, form, authHeader);
        toast.success('QR code created');
      }
      setDialogOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete QR code "${item.name}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/qr-codes/${item.id}`, authHeader);
      toast.success('Deleted');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const downloadPng = async (item, style = 'framed') => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/qr-codes/${item.id}/image.png?style=${style}`, {
        ...authHeader,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.name.replace(/[^a-z0-9-_]+/gi, '_')}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const copyScanUrl = (item) => {
    const baseHost = BACKEND_URL.replace(/\/api\/?$/, '');
    const scanUrl = `${baseHost}/api/qr/${item.id}/scan`;
    navigator.clipboard.writeText(scanUrl);
    toast.success('Scan URL copied');
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white p-3 sm:p-4 md:p-6" data-testid="qr-codes-page">
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/')}
              className="border-slate-600 bg-slate-800 hover:bg-slate-700 text-white">
              <ArrowLeft className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Back</span>
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold flex items-center gap-2">
                <QrCode className="w-6 h-6 text-cyan-400" /> QR Codes
              </h1>
              <p className="text-xs text-slate-400">
                Create scannable QR codes with the BOH logo · scans are tracked
              </p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="qr-new-btn">
            <Plus className="w-4 h-4 mr-1" /> New QR
          </Button>
        </div>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">All QR Codes</CardTitle>
            <CardDescription className="text-slate-400">
              {items.length} total · {items.reduce((s, i) => s + (i.scan_count || 0), 0)} total scans
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 text-center text-slate-400">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-slate-400">No QR codes yet. Click <strong>New QR</strong>.</div>
            ) : (
              <div className="divide-y divide-slate-700">
                {items.map((q) => (
                  <div key={q.id} className="p-3 sm:p-4 flex flex-col md:flex-row md:items-center gap-3"
                    data-testid={`qr-row-${q.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-white text-base truncate">{q.name}</h3>
                        <Badge className="bg-cyan-700 hover:bg-cyan-700">
                          {q.scan_count || 0} scan{q.scan_count === 1 ? '' : 's'}
                        </Badge>
                      </div>
                      <a href={q.target_url} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-blue-400 hover:underline break-all">
                        → {q.target_url}
                      </a>
                      {q.last_scanned_at && (
                        <div className="text-[10px] text-slate-500 mt-1">
                          Last scan: {new Date(q.last_scanned_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-wrap shrink-0">
                      <Button size="sm" variant="outline" onClick={() => setPreviewItem(q)}
                        className="border-slate-500 bg-slate-700 hover:bg-slate-600 text-white"
                        data-testid={`qr-view-${q.id}`}>
                        <Eye className="w-3 h-3 sm:mr-1" /><span className="hidden sm:inline">View</span>
                      </Button>
                      <Button size="sm" onClick={() => downloadPng(q)}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid={`qr-download-${q.id}`}>
                        <Download className="w-3 h-3 sm:mr-1" /><span className="hidden sm:inline">PNG</span>
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => copyScanUrl(q)}
                        className="border-slate-500 bg-slate-700 hover:bg-slate-600 text-white"
                        aria-label="Copy scan URL">
                        <Copy className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(q)}
                        className="border-slate-500 bg-slate-700 hover:bg-slate-600 text-white"
                        aria-label="Edit">
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => remove(q)}
                        className="border-red-700 bg-red-900/40 hover:bg-red-900/60 text-red-300"
                        aria-label="Delete" data-testid={`qr-delete-${q.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create / Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white w-[95vw] max-w-md p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit QR Code' : 'New QR Code'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              The QR routes through the hub so we can count scans before redirecting.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. HA Chapter Sign-up 2026" className="bg-slate-700 border-slate-600 text-white"
                data-testid="qr-name-input" />
            </div>
            <div>
              <Label>Target URL</Label>
              <Input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })}
                placeholder="https://example.com/signup" className="bg-slate-700 border-slate-600 text-white"
                data-testid="qr-url-input" />
              <p className="text-xs text-slate-500 mt-1">Must start with http:// or https://</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}
              className="border-slate-600 bg-slate-700 hover:bg-slate-600 text-white">Cancel</Button>
            <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="qr-save-btn">
              {editing ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview */}
      <Dialog open={!!previewItem} onOpenChange={() => setPreviewItem(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white w-[95vw] max-w-md p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>{previewItem?.name}</DialogTitle>
            <DialogDescription className="text-slate-400 break-all">
              → {previewItem?.target_url}
            </DialogDescription>
          </DialogHeader>
          {previewItem && (
            <div className="flex flex-col items-center gap-3">
              <div className="flex gap-1" data-testid="qr-style-toggle">
                {[
                  ['framed', 'Framed (border + text)'],
                  ['plain', 'Plain QR'],
                ].map(([key, label]) => (
                  <button key={key} onClick={() => setPreviewStyle(key)}
                    data-testid={`qr-style-${key}`}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                      previewStyle === key
                        ? 'bg-amber-600 border-amber-500 text-white'
                        : 'bg-slate-900/60 border-slate-700 text-slate-400 hover:text-white'
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
              {previewSrc ? (
                <img
                  src={previewSrc}
                  alt={previewItem.name}
                  className="w-full max-w-xs bg-white rounded-lg"
                  style={{ imageRendering: 'pixelated' }}
                  data-testid="qr-preview-image"
                />
              ) : (
                <div className="w-full max-w-xs aspect-square bg-slate-700 rounded-lg flex items-center justify-center text-slate-400 text-sm">
                  Loading QR…
                </div>
              )}
              <Button onClick={() => downloadPng(previewItem, previewStyle)} className="bg-blue-600 hover:bg-blue-700 w-full">
                <Download className="w-4 h-4 mr-1" /> Download PNG
              </Button>
              {previewScanUrl && (
                <div
                  className="w-full bg-slate-900/70 border border-slate-700 rounded-md p-2 text-xs text-slate-300 break-all"
                  data-testid="qr-scan-url-block"
                >
                  <div className="text-slate-500 uppercase tracking-wide text-[10px] font-semibold mb-1">
                    Encoded scan URL
                  </div>
                  <a
                    href={previewScanUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-emerald-400 hover:underline"
                    data-testid="qr-scan-url-link"
                  >
                    {previewScanUrl}
                  </a>
                  <div className="text-slate-500 text-[10px] mt-1">
                    Click to test the redirect — if this works, scanning the QR works too.
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default QRCodeManager;
